// GTM dataLayer helpers. Pushes are best-effort and SSR-safe.
//
// Runs alongside the existing trackEvent middleware (lib/booking/analytics.ts)
// and does not replace it.

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

function getLayer(): Record<string, unknown>[] | null {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

export function pushDataLayer(obj: Record<string, unknown>): void {
  const dl = getLayer();
  if (!dl) return;
  try {
    dl.push(obj);
  } catch {
    // swallow
  }
}

export function pushEcommerce(
  eventName: string,
  ecommerce: Record<string, unknown>,
): void {
  const dl = getLayer();
  if (!dl) return;
  try {
    // Reset so item data from one event never leaks into the next.
    dl.push({ ecommerce: null });
    dl.push({ event: eventName, ecommerce });
  } catch {
    // swallow
  }
}

export async function sha256(value: string): Promise<string> {
  if (!value) return "";
  if (typeof crypto === "undefined" || !crypto.subtle) return "";
  try {
    const buf = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(value),
    );
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
  );
  return m ? decodeURIComponent(m[1]) : "";
}

const ATTRIBUTION_KEY = "sons_island_attribution_v1";

export type Attribution = {
  gclid: string | null;
  fbclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbp: string | null;
  fbc: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  landing_url: string | null;
  landing_referrer: string | null;
  captured_at: string | null;
};

const ATTRIBUTION_FIELDS: Array<keyof Attribution> = [
  "gclid",
  "fbclid",
  "gbraid",
  "wbraid",
  "fbp",
  "fbc",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "landing_url",
  "landing_referrer",
  "captured_at",
];

function emptyAttribution(): Attribution {
  return ATTRIBUTION_FIELDS.reduce((acc, k) => {
    acc[k] = null;
    return acc;
  }, {} as Attribution);
}

function readStored(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    const out = emptyAttribution();
    for (const k of ATTRIBUTION_FIELDS) {
      const v = parsed[k];
      if (typeof v === "string" && v.length > 0) out[k] = v;
    }
    return out;
  } catch {
    return null;
  }
}

function writeStored(attr: Attribution): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attr));
  } catch {
    // swallow
  }
}

/**
 * First-touch attribution capture. Persists once per browser (localStorage).
 * On subsequent calls, only backfills `fbp`/`fbc` from cookies if they were
 * empty at capture time — Meta Pixel often sets these after first paint.
 * UTMs and click IDs are never overwritten.
 */
export function captureAttribution(): Attribution {
  if (typeof window === "undefined") return emptyAttribution();

  const existing = readStored();
  if (existing) {
    let changed = false;
    if (!existing.fbp) {
      const fbp = readCookie("_fbp");
      if (fbp) {
        existing.fbp = fbp;
        changed = true;
      }
    }
    if (!existing.fbc) {
      const fbc = readCookie("_fbc");
      if (fbc) {
        existing.fbc = fbc;
        changed = true;
      }
    }
    if (changed) writeStored(existing);
    pushDataLayer({ attribution: existing });
    return existing;
  }

  const params = new URLSearchParams(window.location.search);
  const get = (k: string) => {
    const v = params.get(k);
    return v && v.length > 0 ? v : null;
  };

  const ref = typeof document !== "undefined" ? document.referrer : "";
  const attr: Attribution = {
    gclid: get("gclid"),
    fbclid: get("fbclid"),
    gbraid: get("gbraid"),
    wbraid: get("wbraid"),
    fbp: readCookie("_fbp") || null,
    fbc: readCookie("_fbc") || null,
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_content: get("utm_content"),
    utm_term: get("utm_term"),
    landing_url: window.location.href || null,
    landing_referrer: ref || null,
    captured_at: new Date().toISOString(),
  };

  writeStored(attr);
  pushDataLayer({ attribution: attr });
  return attr;
}

/**
 * Returns the persisted first-touch attribution snapshot, or a null-filled
 * skeleton if capture hasn't run yet. Safe on SSR.
 */
export function getAttribution(): Attribution {
  return readStored() ?? emptyAttribution();
}